import axios from "axios";
import {
  BASE_PROMPT,
  BASE_SYSTEM_PROMPT,
  buildUserContent,
  PdfExcerpt,
  RunPromptOptions,
  RunPromptResult,
} from "./aiCommon";
import { Reference } from "./reference";

/**
 * Call a local LM Studio server using its OpenAI-compatible Chat Completions API.
 * Default base URL: http://localhost:2000
 * Default model: "qwen/qwen3-vl-30b" (adjust to whatever model name LM Studio serves)
 *
 * Env vars (optional):
 * - LMSTUDIO_BASE_URL (e.g., http://localhost:2000)
 * - LMSTUDIO_MODEL (overrides default model)
 */
export async function runPromptLmStudio(
  sourceDocument: PdfExcerpt,
  reference: Reference,
  options: RunPromptOptions = {}
): Promise<RunPromptResult> {
  const baseUrl = (
    process.env.LMSTUDIO_BASE_URL || "http://localhost:2000"
  ).replace(/\/$/, "");
  const endpoint = `${baseUrl}/v1/chat/completions`;

  const modelToUse = "deepseek/deepseek-r1-0528-qwen3-8b";
  // const modelToUse = "qwen/qwen3-vl-30b"

  const model = options.model || process.env.LMSTUDIO_MODEL || modelToUse;
  const temperature =
    typeof options.temperature === "number" ? options.temperature : 1;
  const { maxTokens } = options;
  const systemMessage = options.system || BASE_SYSTEM_PROMPT;

  const basePrompt = BASE_PROMPT;
  const maxInputChars = options.maxInputChars || 160000; // soft guardrail
  const { content: built, truncated } = buildUserContent(
    basePrompt,
    sourceDocument,
    reference,
    maxInputChars
  );
  // LM Studio path stays text-only; ignore attached images if present.
  const userContent =
    typeof built === "string"
      ? built
      : built
          .filter((p): p is { type: "text"; text: string } => p.type === "text")
          .map((p) => p.text)
          .join("\n");

  const payload = {
    model,
    temperature,
    max_tokens: maxTokens,
    messages: [
      { role: "system", content: systemMessage },
      { role: "user", content: userContent },
    ],
  };

  const { data } = await axios.post(endpoint, payload, {
    headers: {
      "Content-Type": "application/json",
      // No auth header needed for local LM Studio by default
    },
    timeout: 720000 * 4,
  });

  const choice = data?.choices?.[0];
  const text: string = choice?.message?.content || "";

  return {
    text,
    fileName: sourceDocument.fileName,
    model: data?.model || model,
    usage: data?.usage,
    raw: data,
    reference,
    ...(truncated ? { inputTruncated: true } : {}),
  };
}

export default runPromptLmStudio;
